"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { FiShield } from "react-icons/fi"
import api from "../../../libs/axios"
import toast from "react-hot-toast"

interface Props {
  onComplete: (data: any) => void
  onBack: () => void
  matchData: any
}

interface Team {
  id: number
  name: string
  short_name: string
}

export default function ConductToss({ onComplete, onBack, matchData }: Props) {
  const [formData, setFormData] = useState({
    toss_winner_team_id: matchData.toss_winner_team_id || "",
    toss_decision: (matchData.toss_decision || "").toString().toUpperCase() || "",
  })
  const [coinTossed, setCoinTossed] = useState(false)
  const [coinFlipping, setCoinFlipping] = useState(false)
  const [coinResult, setCoinResult] = useState<"heads" | "tails" | null>(null)
  const [errors, setErrors] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [teams, setTeams] = useState<Team[]>([])

  useEffect(() => {
    fetchTeams()
    // prefill teams and toss info on edit
    setFormData((prev) => ({
      ...prev,
      toss_winner_team_id:
        matchData?.toss_winner_team_id != null ? matchData.toss_winner_team_id.toString() : prev.toss_winner_team_id,
      toss_decision: ((matchData?.toss_decision ?? prev.toss_decision) || "").toString().toUpperCase(),
    }))
    if (matchData?.toss_winner_team_id || matchData?.toss_decision) {
      setCoinTossed(true)
    }
  }, [matchData])

  const fetchTeams = async () => {
    try {
      const [teamAResponse, teamBResponse] = await Promise.all([
        api.get(`/teams/${matchData.team_a_id}`),
        api.get(`/teams/${matchData.team_b_id}`),
      ])

      setTeams([
        {
          id: teamAResponse.data.team.id,
          name: teamAResponse.data.team.name,
          short_name: teamAResponse.data.team.name,
        },
        {
          id: teamBResponse.data.team.id,
          name: teamBResponse.data.team.name,
          short_name: teamBResponse.data.team.name,
        },
      ])
    } catch (error) {
      console.error("Error fetching teams:", error)
      toast.error("Failed to load teams")
    }
  }

  const handleCoinTap = () => {
    if (coinFlipping) return

    setCoinFlipping(true)
    setCoinTossed(true)

    // Simulate coin flip animation
    const flipDuration = 2000 // 2 seconds
    const flipInterval = 100 // Change face every 100ms

    let flips = 0
    const maxFlips = flipDuration / flipInterval

    const flipCoin = setInterval(() => {
      flips++
      setCoinResult(flips % 2 === 0 ? "heads" : "tails")

      if (flips >= maxFlips) {
        clearInterval(flipCoin)
        // Set final result (random heads or tails)
        const finalResult = Math.random() > 0.5 ? "heads" : "tails"
        setCoinResult(finalResult)
        setCoinFlipping(false)

        // Show result message
        toast.success(`It's ${finalResult.toUpperCase()}!`)
      }
    }, flipInterval)
  }

  const validateForm = () => {
    const newErrors: any = {}

    if (!formData.toss_winner_team_id) {
      newErrors.toss_winner_team_id = "Please select who won the toss"
    }

    if (!formData.toss_decision) {
      newErrors.toss_decision = "Please select batting or bowling"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      await api.put(`/matches/${matchData.match_id}/toss`, {
        toss_winner_team_id: Number.parseInt(formData.toss_winner_team_id.toString()),
        toss_decision: formData.toss_decision.toLowerCase(),
      })
      toast.success("Toss Conducted Successfully")

      onComplete(formData)
    } catch (error: any) {
      if (error.response) {
        console.error("API Error:", error.response.data)
        toast.error(error.response.data.message || "Failed to conduct toss")
      } else {
        console.error("Network Error:", error)
        toast.error("Network error. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Conduct Toss</h2>
        <p className="text-gray-600">Flip the coin and select the toss winner's decision</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Coin Toss Section */}
        <div className="text-center">
          <div className="relative w-32 h-32 mx-auto mb-4 perspective-1000">
            <div
              className={`relative w-full h-full preserve-3d transition-transform duration-500 ${coinFlipping ? "animate-flip" : ""}`}
              style={{
                transform: coinResult === "heads" ? "rotateY(0deg)" : "rotateY(180deg)",
                animation: coinFlipping ? "flip 2s ease-out forwards" : "none",
              }}
            >
              <div className="absolute w-full h-full backface-hidden rounded-full bg-yellow-400 border-4 border-yellow-500 flex items-center justify-center text-4xl">
                <span>H</span>
              </div>
              <div className="absolute w-full h-full backface-hidden rounded-full bg-yellow-400 border-4 border-yellow-500 flex items-center justify-center text-4xl rotate-y-180">
                <span>T</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCoinTap}
            disabled={coinFlipping}
            className={`px-6 py-2 rounded-md ${coinFlipping ? "bg-gray-400" : "bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"} text-white transition-colors`}
          >
            {coinFlipping ? "Flipping..." : "Flip Coin"}
          </button>

          {coinResult && !coinFlipping && (
            <p className="mt-4 text-green-600 font-medium">Result: {coinResult.toUpperCase()}</p>
          )}
        </div>

        {/* Who won the toss section */}
        {coinTossed && teams.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Who won the toss?</h3>
            <div className="space-y-3">
              {teams.map((team) => (
                <label
                  key={team.id}
                  className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="radio"
                    name="toss_winner"
                    value={team.id}
                    checked={formData.toss_winner_team_id.toString() === team.id.toString()}
                    onChange={(e) => setFormData((prev) => ({ ...prev, toss_winner_team_id: e.target.value }))}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <div className="ml-4 flex items-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-sm font-bold text-primary-600">
                        {team.name
                          .split(" ")
                          .map((word) => word[0])
                          .join("")}
                      </span>
                    </div>
                    <span className="text-gray-900 font-medium">{team.name}</span>
                  </div>
                </label>
              ))}
            </div>
            {errors.toss_winner_team_id && <p className="mt-2 text-sm text-red-600">{errors.toss_winner_team_id}</p>}
          </div>
        )}

        {/* Toss decision section */}
        {formData.toss_winner_team_id && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Winner of the toss selected to?</h3>
            <div className="space-y-3">
              <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="toss_decision"
                  value="BAT"
                  checked={formData.toss_decision === "BAT"}
                  onChange={(e) => setFormData((prev) => ({ ...prev, toss_decision: e.target.value }))}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-4 flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-lg">🏏</span>
                  </div>
                  <span className="text-gray-900 font-medium">Batting</span>
                </div>
              </label>

              <label className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input
                  type="radio"
                  name="toss_decision"
                  value="BOWL"
                  checked={formData.toss_decision === "BOWL"}
                  onChange={(e) => setFormData((prev) => ({ ...prev, toss_decision: e.target.value }))}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                />
                <div className="ml-4 flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-lg">⚾</span>
                  </div>
                  <span className="text-gray-900 font-medium">Bowling</span>
                </div>
              </label>
            </div>
            {errors.toss_decision && <p className="mt-2 text-sm text-red-600">{errors.toss_decision}</p>}
          </div>
        )}

        {/* Summary section */}
        {formData.toss_winner_team_id && formData.toss_decision && (
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center">
              <FiShield className="w-5 h-5 text-blue-600 mr-2" />
              <div>
                <p className="font-medium text-blue-900">Toss Summary</p>
                <p className="text-sm text-blue-700">
                  {teams.find((t) => t.id.toString() === formData.toss_winner_team_id.toString())?.name} wins the toss
                  and chooses to {formData.toss_decision === "BAT" ? "bat first" : "bowl first"}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-6 border-t">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Back to Match Rules
          </button>
          <button
            type="submit"
            disabled={loading || !formData.toss_winner_team_id || !formData.toss_decision}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Conducting Toss..." : "Start Match"}
          </button>
        </div>
      </form>
    </div>
  )
}
